import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdCondition
} from '@bindoc-projects/condition-domain-lib';
import { BdArrayUtil, BdEquals } from '@bindoc-projects/util-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const FilterConditionDrivingTimePluginType: FilterConditionPluginSpecification<
  DoubleRange[],
  FilterConditionDrivingTimeDto,
  FilterConditionDrivingTimeConditionJson
> = {
  type: 'drivingTime',
  category: 'case',
  displayName: { de: 'Erreichbarkeit innerhalb Fahrzeit' },
  sortingIndex: 8,
  occurrence: 'multiple',
  fromObject(
    obj: FilterConditionDrivingTimeDto,
  ): FilterConditionPluginImpl<DoubleRange[]> {
    return new FilterConditionDrivingTimePlugin(obj.includeRanges);
  },
  fromCondition(
    jsonObj: FilterConditionDrivingTimeConditionJson,
  ): FilterConditionPluginImpl<DoubleRange[]> {
    return new FilterConditionDrivingTimePlugin(
        jsonObj.includes
          ? jsonObj.includes.map((range) => ({
              min: range.from,
              max: range.to,
            }))
          : [],
      );
  },
  newInstance(): FilterConditionPluginImpl<DoubleRange[]> {
    return new FilterConditionDrivingTimePlugin([]);
  },
};

export class FilterConditionDrivingTimePlugin extends FilterConditionPluginImpl<
  DoubleRange[],
  FilterConditionDrivingTimeDto
> {
  static override Specification: FilterConditionPluginSpecification<
    DoubleRange[],
    FilterConditionDrivingTimeDto,
    FilterConditionDrivingTimeConditionJson
  > = FilterConditionDrivingTimePluginType;

  constructor(private includeRanges: DoubleRange[]) {
    super(FilterConditionDrivingTimePluginType.type);
  }

  toCondition(): BdCondition {
    return new FilterConditionDrivingTimeCondition(this.includeRanges);
  }

  toJSON(): FilterConditionDrivingTimeDto | null {
    return {
      type: this.type,
      includeRanges: this.includeRanges,
    };
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.includeRanges.map(
      (range) => `${range.min} - ${range.max} min`,
    );
    return { title, items };
  }

  toText(): string | null {
    return `Erreichbarkeit innerhalb einer Fahrzeit`;
  }

  toValue(): DoubleRange[] {
    return this.includeRanges;
  }

  override equals(other: FilterConditionDrivingTimePlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(
        this.includeRanges,
        other.includeRanges,
        (a, b) =>
          BdEquals.equals(a.min, b.min) && BdEquals.equals(a.max, b.max),
      )
    );
  }

  override validationToText(): string {
    if (this.includeRanges.length === 0) {
      return 'Mindestens eine Erreichbarkeit auswählen';
    } else {
      return '';
    }
  }

  clone(): FilterConditionDrivingTimePlugin {
    return new FilterConditionDrivingTimePlugin(
      this.includeRanges.map((range) => JSON.parse(JSON.stringify(range))),
    );
  }
}

export class FilterConditionDrivingTimeCondition extends BdCondition {
  constructor(private _includeRanges: DoubleRange[]) {
    super('drivingTime');
  }

  toJSON(): FilterConditionDto {
    return {
      [this.id]: <FilterConditionDrivingTimeConditionJson>{
        includes: this._includeRanges.map((range) => ({
          from: range.min,
          to: range.max,
        })),
      },
    };
  }
}

interface FilterConditionDrivingTimeDto {
  type: string;
  includeRanges: DoubleRange[];
}

interface FilterConditionDrivingTimeConditionJson {
  includes: { from: number; to: number }[];
}
